import * as React from 'react';

export type UseDialogStateHookResult<T> = {
  isOpen: boolean;
  open: (extraData?: T) => void;
  reset: () => void;
  extraData?: T;
};

type DialogState<T> = {
  isOpen: boolean;
  extraData: T | undefined;
};

enum DialogActionType {
  OPEN = 'OPEN',
  RESET = 'RESET',
}

type DialogAction<T> = {
  type: DialogActionType;
  extraData?: T;
};

function modalReducer<T>(state: DialogState<T>, action: DialogAction<T>): DialogState<T> {
  switch (action.type) {
    case DialogActionType.OPEN:
      return {
        isOpen: true,
        extraData: action.extraData,
      };

    case DialogActionType.RESET:
      return {
        isOpen: false,
        extraData: undefined,
      };

    default:
      return state;
  }
}

export function useDialogState<T>(initialExtraData?: T): UseDialogStateHookResult<T> {
  const [{ isOpen, extraData }, dispatch] = React.useReducer<React.Reducer<DialogState<T>, DialogAction<T>>>(
    modalReducer,
    { isOpen: false, extraData: initialExtraData }
  );

  const open = React.useCallback(
    (data: T): void => {
      dispatch({ type: DialogActionType.OPEN, extraData: data });
    },
    [dispatch]
  );

  const reset = React.useCallback((): void => {
    dispatch({ type: DialogActionType.RESET });
  }, [dispatch]);

  return {
    isOpen,
    open,
    reset,
    extraData,
  };
}
